import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, finalize, Observable, tap, throwError } from 'rxjs';
import {
  CallInformation,
  CallsQueryParams,
  ColumnPrefs,
  COLUMN_PREFS_STORAGE_KEY,
  DEFAULT_VISIBLE_FIELDS,
} from './call-information.model';
import { environment } from '../../../environments/environment';
/** Error shape surfaced to the component. */
export interface ApiError {
  status: number;
  message: string;
  correlationId?: string;
}

/**
 * Canonical set of URL/query fields that identify a call search — the SINGLE source of truth.
 * `callsQueryKey` iterates it, and `syncQueryParams` types its record against it (compile error
 * if a field is added here but not written to the URL, or vice versa), so the reuse key and the
 * URL can never silently diverge. Order is fixed so the key is stable.
 * NOTE: `calledNumber` is intentionally excluded — it is not a UI filter (never enters the URL).
 * The `phoneServiceId`↔`userAccountId` form-control aliasing is handled in hydrateFromQueryParams.
 */
export const CALL_FILTER_FIELDS = [
  'customerId', 'userAccountId', 'phoneServiceId', 'agentId', 'singleDate', 'start', 'end',
] as const;

export type CallFilterField = (typeof CALL_FILTER_FIELDS)[number];

/**
 * EB-4038: build a stable signature of a call search from its URL query fields.
 * Both the URL (on area-switch return) and a live search feed the same reader, so
 * the component can tell whether the already-loaded rows match the URL and skip a refetch.
 * Empty/whitespace values are dropped so `?customerId=13279` and `?customerId=13279&agentId=`
 * produce the same key.
 */
export function callsQueryKey(get: (field: CallFilterField) => string | null | undefined): string {
  return CALL_FILTER_FIELDS
    .map((field) => [field, (get(field) ?? '').trim()] as const)
    .filter(([, value]) => value !== '')
    .map(([field, value]) => `${field}=${value}`)
    .join('&');
}

/**
 * CallsService — fetches call information for a RAV over a date range,
 * manages signal-based UI state (rows, loading, error) and persists
 * column preferences in localStorage.
 *
 * Backend contract: GET /phoneservice/v3/calls/callinformation
 *   start, end:      yyyy-MM-dd'T'HH:mm:ss.SSS (local time, no Z) — optional
 *   customerId:      string  (optional)
 *   agentId:         integer (optional — employee number)
 *   userAccountId:   integer (optional — mutually exclusive with phoneServiceId in UI)
 *   phoneServiceId:  integer (optional — mutually exclusive with userAccountId in UI)
 *   calledNumber:    string  (optional — sent verbatim)
 *
 * Any unset/empty value is omitted from the request entirely.
 */
@Injectable({ providedIn: 'root' })
export class CallsService {
  private readonly http = inject(HttpClient);

  readonly rows = signal<CallInformation[]>([]);
  readonly loading = signal(false);
  readonly error = signal<ApiError | null>(null);

  /**
   * Wall-clock time the current `rows` were fetched from the backend. Stamped on a
   * successful load, reset by `clear()`. On area-switch reuse the rows (and this
   * timestamp) survive untouched, so the Call Viewer can show "loaded HH:mm" and make
   * the snapshot age explicit instead of silently serving stale data (EB-4038).
   */
  readonly loadedAt = signal<Date | null>(null);

  /**
   * EB-4038: query signature (see {@link callsQueryKey}) that produced the current `rows`.
   * Set on a successful search, reset by `clear()`. The Call Viewer compares it against the
   * URL on entry to decide whether returning from another area can reuse the loaded rows.
   */
  readonly loadedQueryKey = signal<string | null>(null);

  findCallInformation(params: CallsQueryParams): Observable<CallInformation[]> {
    this.loading.set(true);
    this.error.set(null);

    const httpParams: Record<string, string> = {};
    if (params.start) {
      httpParams['start'] = this.formatApiDate(params.start);
    }
    if (params.end) {
      httpParams['end'] = this.formatApiDate(params.end);
    }
    if (params.customerId) {
      httpParams['customerId'] = params.customerId;
    }
    if (params.agentId != null) {
      httpParams['agentId'] = String(params.agentId);
    }
    if (params.userAccountId != null) {
      httpParams['userAccountId'] = String(params.userAccountId);
    }
    if (params.phoneServiceId != null) {
      httpParams['phoneServiceId'] = String(params.phoneServiceId);
    }
    if (params.calledNumber) {
      httpParams['calledNumber'] = params.calledNumber;
    }

    return this.http
      .get<CallInformation[]>(`${environment.phoneServiceUrl}/calls/callinformation`, {
        params: httpParams,
      })
      .pipe(
        tap((data) => {
          this.rows.set(data ?? []);
          this.loadedAt.set(new Date());
        }),
        catchError((err: HttpErrorResponse) => {
          this.rows.set([]);
          this.loadedAt.set(null);
          this.error.set({
            status: err.status ?? 0,
            message: err.message ?? 'Failed to load calls',
            correlationId: err.headers?.get('correlation-id') ?? undefined,
          });
          return throwError(() => err);
        }),
        finalize(() => this.loading.set(false)),
      );
  }

  /** Reset rows + error (called from component on RAV change). */
  clear(): void {
    this.rows.set([]);
    this.error.set(null);
    this.loadedQueryKey.set(null);
    this.loadedAt.set(null);
  }

  /** EB-4038: record the query signature of the rows now loaded (for area-switch reuse). */
  markLoaded(queryKey: string): void {
    this.loadedQueryKey.set(queryKey);
  }

  /**
   * EB-4038: true when the currently-loaded rows satisfy the given URL query key —
   * i.e. returning to the Call Viewer with this URL can reuse them without a refetch.
   * Single home for the reuse invariant (empty key or no rows never reuses).
   */
  matchesLoaded(urlQueryKey: string): boolean {
    return urlQueryKey !== '' && this.rows().length > 0 && this.loadedQueryKey() === urlQueryKey;
  }

  /**
   * Read persisted column prefs from localStorage.
   * Falls back to an empty visibleFields array when:
   *   - the key is missing
   *   - the JSON is malformed
   *   - the parsed shape is wrong
   * Note: the empty fallback means only default-visible columns show;
   * the component chooses which toggleable columns to display based on this.
   */
  loadColumnPrefs(): ColumnPrefs {
    try {
      const raw = localStorage.getItem(COLUMN_PREFS_STORAGE_KEY);
      if (!raw) return { visibleFields: [] };
      const parsed = JSON.parse(raw) as unknown;
      if (
        parsed &&
        typeof parsed === 'object' &&
        Array.isArray((parsed as ColumnPrefs).visibleFields) &&
        (parsed as ColumnPrefs).visibleFields.every((x) => typeof x === 'string')
      ) {
        return { visibleFields: (parsed as ColumnPrefs).visibleFields };
      }
      return { visibleFields: [] };
    } catch {
      return { visibleFields: [] };
    }
  }

  /** Persist column prefs to localStorage. */
  saveColumnPrefs(prefs: ColumnPrefs): void {
    try {
      localStorage.setItem(COLUMN_PREFS_STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      // Swallow: localStorage may be disabled / quota exceeded — not fatal.
    }
  }

  /**
   * Format a Date to yyyy-MM-dd'T'HH:mm:ss.SSS in local time (no Z).
   * Matches @DateTimeFormat(pattern = "yyyy-MM-dd'T'HH:mm:ss.SSS") on CallController.
   */
  private formatApiDate(d: Date): string {
    const pad = (n: number, len = 2): string => String(n).padStart(len, '0');
    const y = d.getFullYear();
    const mo = pad(d.getMonth() + 1);
    const da = pad(d.getDate());
    const h = pad(d.getHours());
    const mi = pad(d.getMinutes());
    const s = pad(d.getSeconds());
    const ms = pad(d.getMilliseconds(), 3);
    return `${y}-${mo}-${da}T${h}:${mi}:${s}.${ms}`;
  }
}

/** Re-export DEFAULT_VISIBLE_FIELDS so callers can import from this service. */
export { DEFAULT_VISIBLE_FIELDS };
